import React, { useEffect, useRef, useState } from "react";
import { HOMEPAGE_SCREENSHOTS } from "../../constants/homepageScreenshots";

/**
 * "How RunCards works" — single-screen slideshow that auto-advances through
 * the 4 product capabilities. Phone frame stays put; the screen inside fades
 * between screenshots while the copy panel beside it fades in sync.
 *
 * Controls: prev/next arrows + clickable dot strip (with progress fill).
 * Auto-advance keeps running regardless of hover — it only pauses if the user
 * has manually clicked a dot/arrow in the last AUTO_ADVANCE_MS window
 * (resetting the timer is what manual interaction does, not a freeze).
 */

const SLIDES = [
  {
    kicker: "01 · Discover",
    title: "Find runs anywhere.",
    body: "Map your city. See what's on this week. Filter by distance, pace, surface, time.",
    screenshot: HOMEPAGE_SCREENSHOTS.newYorkMap,
  },
  {
    kicker: "02 · Show up",
    title: "One tap to register.",
    body: "Meeting cards with the address, the pace, the vibe — and a one-tap Join This Run.",
    screenshot: HOMEPAGE_SCREENSHOTS.runclub86Meeting,
  },
  {
    kicker: "03 · Belong",
    title: "Real clubs, real community.",
    body: "Each club has a magazine-style profile — the people, the runs, the place.",
    screenshot: HOMEPAGE_SCREENSHOTS.orchardClub,
  },
  {
    kicker: "04 · Your runcard",
    title: "The card other runners see.",
    body: "Pace, story, photos. Build your map. Stamp the cities and clubs you run with.",
    screenshot: HOMEPAGE_SCREENSHOTS.runcardMenu,
  },
];

const AUTO_ADVANCE_MS = 4500;

function ArrowButton({ direction, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={direction === "prev" ? "Previous slide" : "Next slide"}
      // Hidden under sm to keep the phone + arrows from overflowing on the
      // smallest phones (~375px). The dot strip below handles nav on mobile.
      className="rc-glass group hidden h-11 w-11 items-center justify-center rounded-full transition-transform hover:scale-105 sm:flex"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ transform: direction === "prev" ? "rotate(180deg)" : "none" }}
      >
        <path d="M5 12h14M13 6l6 6-6 6" />
      </svg>
    </button>
  );
}

const HowItWorksSection = () => {
  const [active, setActive] = useState(0);
  const intervalRef = useRef(null);

  // Auto-advance loop — restarts whenever `active` changes so manual jumps
  // reset the 4.5s window instead of advancing immediately.
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setActive((i) => (i + 1) % SLIDES.length);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(intervalRef.current);
  }, [active]);

  const go = (i) => setActive(((i % SLIDES.length) + SLIDES.length) % SLIDES.length);

  return (
    <section className="relative overflow-hidden bg-black text-white">
      {/* ambient gradient */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(120,127,154,0.18),transparent_55%),radial-gradient(circle_at_bottom_left,rgba(58,106,76,0.12),transparent_50%)]" />

      <div className="relative mx-auto max-w-[1280px] px-5 py-12 sm:px-8 sm:py-16 md:px-12 md:py-20">
        {/* Section header */}
        <div className="mb-12 max-w-3xl md:mb-14">
          <p className="mb-3 font-runcards-bold text-[11px] uppercase tracking-[0.32em] text-white/45">
            How it works
          </p>
          <h2 className="font-runcards-black text-[clamp(2rem,5vw,3.75rem)] leading-[1.0] tracking-[-0.025em]">
            Built for runners.
            <br />
            <span className="text-white/70">Powered by clubs.</span>
          </h2>
        </div>

        {/* Slide stage: phone (with side arrows) + crossfading copy */}
        <div className="grid items-center gap-12 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] md:gap-16">
          {/* Phone column with flanking arrows */}
          <div
            className="flex items-center justify-center gap-3 sm:gap-4 md:justify-start"
            style={{ perspective: "1400px" }}
          >
            <ArrowButton direction="prev" onClick={() => go(active - 1)} />

            <div className="relative w-full max-w-[210px] sm:max-w-[230px]">
              {/* Ambient glow behind the phone — soft white/grey spotlight.
                  No negative z-index (Safari + stacking context bugs). The
                  glow is the FIRST child so DOM order puts the phone on top. */}
              <div className="pointer-events-none absolute -inset-x-24 -inset-y-20">
                {/* Soft grey ellipse — the "blob" */}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "radial-gradient(ellipse at 50% 50%, rgba(230,232,240,0.42), rgba(180,184,196,0.18) 45%, transparent 72%)",
                    filter: "blur(50px)",
                  }}
                />
                {/* Brighter white core directly behind the phone */}
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "radial-gradient(circle at 50% 45%, rgba(255,255,255,0.55), rgba(255,255,255,0.15) 30%, transparent 58%)",
                    filter: "blur(30px)",
                  }}
                />
              </div>

              {/* Tilted, floating phone */}
              <div
                className="relative aspect-[9/19.5] overflow-hidden rounded-[2.5rem] border-[10px] border-neutral-900 bg-neutral-900"
                style={{
                  transform: "rotateY(-9deg) rotateX(4deg)",
                  transformStyle: "preserve-3d",
                  boxShadow:
                    "0 35px 80px -15px rgba(0,0,0,0.75), 0 0 70px rgba(255,255,255,0.10), 0 0 1px 1px rgba(255,255,255,0.06) inset",
                  animation: "rc-hiw-float 7s ease-in-out infinite",
                }}
              >
                {SLIDES.map((slide, i) => (
                  <img
                    key={slide.screenshot.id}
                    src={slide.screenshot.url}
                    alt={slide.screenshot.label}
                    loading={i === 0 ? "eager" : "lazy"}
                    className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-700 ease-in-out ${
                      i === active ? "opacity-100" : "opacity-0"
                    }`}
                  />
                ))}

                {/* Subtle screen glare — diagonal light streak across the top-right.
                    Gives the phone its "real device" specular highlight. */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 opacity-50"
                  style={{
                    background:
                      "linear-gradient(120deg, transparent 40%, rgba(255,255,255,0.10) 50%, transparent 60%)",
                  }}
                />
              </div>
            </div>

            <ArrowButton direction="next" onClick={() => go(active + 1)} />
          </div>

          {/* Crossfading copy + its slide indicator, grouped under the text */}
          <div className="text-center md:text-left">
            {/* Copy — slides crossfade over a reserved-height box */}
            <div className="relative min-h-[150px] md:min-h-[160px]">
              {SLIDES.map((slide, i) => (
                <div
                  key={slide.kicker}
                  className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                    i === active ? "opacity-100" : "pointer-events-none opacity-0"
                  }`}
                >
                  <p className="mb-3 font-runcards-bold text-[11px] uppercase tracking-[0.32em] text-runcards-accent">
                    {slide.kicker}
                  </p>
                  <h3 className="mb-4 font-runcards-black text-[clamp(1.75rem,3.5vw,2.5rem)] leading-[1.05] tracking-[-0.02em]">
                    {slide.title}
                  </h3>
                  <p className="mx-auto max-w-md font-runcards text-base leading-relaxed text-white/65 md:mx-0 md:text-lg">
                    {slide.body}
                  </p>
                </div>
              ))}
            </div>

            {/* Slide indicator — directly under the copy */}
            <div className="mx-auto mt-6 flex w-full max-w-md items-center gap-3 md:mx-0">
              {SLIDES.map((slide, i) => {
                const isActive = i === active;
                return (
                  <button
                    key={slide.kicker}
                    type="button"
                    onClick={() => go(i)}
                    aria-label={`Go to ${slide.title}`}
                    className="group relative flex flex-1 items-center py-3"
                  >
                    <span className="relative block h-1.5 w-full overflow-hidden rounded-full bg-white/15">
                      {isActive ? (
                        <span
                          key={`bar-${active}`}
                          className="absolute inset-y-0 left-0 block bg-runcards-accent"
                          style={{
                            animation: `rc-hiw-progress ${AUTO_ADVANCE_MS}ms linear forwards`,
                          }}
                        />
                      ) : (
                        <span className="absolute inset-y-0 left-0 block w-0 bg-white/30 transition-all duration-200 group-hover:w-full" />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes rc-hiw-progress {
          from { width: 0%; }
          to   { width: 100%; }
        }
        /* Gentle float for the tilted phone — translateY composes with the
           rotateY/rotateX on the element so the tilt stays put while it bobs. */
        @keyframes rc-hiw-float {
          0%, 100% { transform: rotateY(-9deg) rotateX(4deg) translateY(0px); }
          50%      { transform: rotateY(-9deg) rotateX(4deg) translateY(-10px); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="rc-hiw-float"] { animation: none !important; }
        }
      `}</style>
    </section>
  );
};

export default HowItWorksSection;
